import { useMutation, useQuery } from "@tanstack/react-query";
import { signOut as signOutFirebase } from "firebase/auth";
import { ArrowUpRight, FileText, LogOut, Mail, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { auth } from "../../lib/firebase";
import { getMe, loginWithPassword, logout } from "../../lib/api";

const adminPath = "/apps/n1master/admin";
const loginPath = `${adminPath}/login`;

export function N1MasterAdminLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () => loginWithPassword(username, password),
    onSuccess: () => { setPassword(""); navigate(adminPath, { replace: true }); },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (username.trim() && password) login.mutate();
  }

  if (localStorage.getItem("steria_admin_token")) return <Navigate to={adminPath} replace />;

  return (
    <main className="n1-admin-login">
      <section className="n1-admin-login-story">
        <Brand />
        <div><span>N1 MASTER</span><h1>Support administration.</h1><p>Private access for the people responsible for N1 Master support and release operations.</p></div>
        <Link to="/apps/n1master/support">Open public support page <ArrowUpRight size={15} /></Link>
      </section>
      <section className="n1-admin-login-panel">
        <div>
          <span className="n1-admin-lock"><ShieldCheck size={23} /></span>
          <h2>Sign in</h2>
          <p>Use the administrator credentials configured for N1 Master.</p>
          {login.isError && <div className="n1-admin-error">Invalid credentials. Please try again.</div>}
          <form onSubmit={submit}>
            <label>Username<input autoComplete="username" disabled={login.isPending} onChange={(event) => setUsername(event.target.value)} required value={username} /></label>
            <label>Password<input autoComplete="current-password" disabled={login.isPending} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} /></label>
            <button className="button button-primary" disabled={login.isPending} type="submit">{login.isPending ? "Signing in…" : "Sign in"}</button>
          </form>
        </div>
      </section>
    </main>
  );
}

export function N1MasterAdminPage() {
  const navigate = useNavigate();
  const user = useQuery({ queryKey: ["n1-master-admin-me"], queryFn: getMe, retry: false });

  async function signOut() {
    await logout();
    await signOutFirebase(auth).catch(() => undefined);
    navigate(loginPath, { replace: true });
  }

  if (!localStorage.getItem("steria_admin_token")) return <Navigate to={loginPath} replace />;
  if (user.isLoading) return <main className="n1-admin-loading">Opening N1 Master administration…</main>;
  if (user.isError || !user.data) return <Navigate to={loginPath} replace />;

  return (
    <main className="n1-admin-dashboard">
      <header>
        <div className="n1-admin-brand"><Brand compact /><span>N1 Master / Support</span></div>
        <button onClick={signOut}><LogOut size={16} /> Sign out</button>
      </header>
      <section className="n1-admin-dashboard-main">
        <span className="n1-kicker">PRIVATE WORKSPACE</span>
        <h1>Welcome back, {user.data.name}.</h1>
        <p>Use this portal to reach the public support resources and keep customer replies consistent with the current N1 Master release process.</p>
        <div className="n1-admin-cards">
          <a href="mailto:steriahouse@gmail.com?subject=N1%20Master%20Support"><Mail size={20} /><span><strong>Open support inbox</strong><small>Reply to account, payment, progress, and technical issues.</small></span><ArrowUpRight size={17} /></a>
          <Link to="/apps/n1master/support"><FileText size={20} /><span><strong>Review support page</strong><small>Check the exact guidance visible to users.</small></span><ArrowUpRight size={17} /></Link>
          <Link to="/apps/n1master/privacy"><ShieldCheck size={20} /><span><strong>Privacy requests</strong><small>Read the published account-data request process.</small></span><ArrowUpRight size={17} /></Link>
        </div>
      </section>
    </main>
  );
}
